import React, { type ReactNode, useCallback, useEffect, useRef, forwardRef } from 'react';
import { cx } from '@compiled/react';
import { CLAUSE_TYPE_TERMINAL } from '@atlaskit/jql-ast';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import type { FieldOption } from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import {
	ContextualAnalyticsData,
	INLINE_DIALOG,
	useAnalyticsEvents,
	fireUIAnalytics,
	type Attributes,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import type { AdvancedContent$data } from '@atlassian/jira-filter-refinement/src/ui/filter-popup/filter-popup-content/ui/advanced/__generated__/AdvancedContent.graphql';
import type {
	useHydrationQueryLoaderQuery$data,
	JiraJqlViewContext,
	JiraJqlScopeInput,
} from '@atlassian/jira-jql-builder-common/src/ui/hydrate-jql-builder/__generated__/useHydrationQueryLoaderQuery.graphql';
import type { JQLBuilderUI$data } from '@atlassian/jira-jql-builder/src/ui/__generated__/JQLBuilderUI.graphql';
import {
	PickerTypes,
	type FieldToRender,
	type OnClearPicker,
	type OnSearchWrapper,
	type OnSwitchToJql,
} from '../../../common/types.tsx';
import ListItemChild from '../../../common/ui/list-item-child/index.tsx';
import { StyledChild } from '../../../common/ui/styled.tsx';
import type { JiraProjectOptionsType } from '../../../common/ui/types.tsx';
import { useActivePopupKey } from '../../../controllers/active-popup/index.tsx';
import { useSendOptionAnalytics } from '../../../utils/send-option-analytics.tsx';
import { useVerticalMode } from '../../../controllers/feature-context.tsx';
import { CascadeSelectPicker } from './cascade-select-picker/index.tsx';
import { DatePickerWrapper } from './date-picker-wrapper/index.tsx';
import { LastUserCommentedPickerWrapper } from './last-user-commented-picker-wrapper/index.tsx';
import { NumberRangePicker } from './number-range-picker/index.tsx';
import { PickerWrapper } from './picker-wrapper/main.tsx';
import { TextAreaPicker } from './text-area-picker/main.tsx';
import { UnsupportedPickerWrapper } from './unsupported-picker-wrapper/index.tsx';

type FieldProps = {
	fieldToRender: FieldToRender;
	fieldsToRender: FieldToRender[];
	loading: boolean;
	hydratedData: useHydrationQueryLoaderQuery$data | JQLBuilderUI$data | AdvancedContent$data;
	onSearchWrapper?: OnSearchWrapper;
	onSwitchToJql?: OnSwitchToJql;
	onClearPicker: OnClearPicker;
	selectedFields: FieldOption[];
	projectOptions?: JiraProjectOptionsType;
	singleSelectFields?: string[];
	shouldUseListMarkup?: boolean;
	hideOperatorDropdown?: boolean;
	contextJql: string;
	viewContext: JiraJqlViewContext | null;
	scope: JiraJqlScopeInput | null;
	suggestedUsersCount?: number;
	isAddingField?: boolean;
	setIsAddingField?: (isAddingField: boolean) => void;
};

const analyticsAttributes = {
	pickerType: 'field',
} as const;

type FieldWrapperBaseProps = {
	children: ReactNode;
	useListElement: Boolean;
	handleKeyDown: (event: React.KeyboardEvent<HTMLSpanElement | HTMLDivElement>) => void;
} & React.HTMLAttributes<HTMLSpanElement | HTMLDivElement>;

const styles = cssMap({
	vertical: {
		display: 'flex',
		flex: 1,
		alignItems: 'center',
	},
});

const FieldWrapperNew = forwardRef<HTMLUListElement, FieldWrapperBaseProps>(
	({ children, useListElement, handleKeyDown }, ref) => {
		const verticalMode = useVerticalMode();

		if (!fg('list_lovability_improving_filters')) {
			return;
		}

		return useListElement ? (
			<ListItemChild data-vc="jql-builder-basic-field-list-item">
				<Box
					as="span"
					ref={ref}
					role="presentation"
					onKeyDown={handleKeyDown}
					xcss={cx(verticalMode && styles.vertical)}
				>
					{children}
				</Box>
			</ListItemChild>
		) : (
			<StyledChild onKeyDown={handleKeyDown}>
				<Box
					as="span"
					ref={ref}
					role="presentation"
					onKeyDown={handleKeyDown}
					xcss={cx(verticalMode && styles.vertical)}
				>
					{children}
				</Box>
			</StyledChild>
		);
	},
);

const FieldWrapperOld = ({ children, useListElement, handleKeyDown }: FieldWrapperBaseProps) => {
	return useListElement ? (
		<ListItemChild data-vc="jql-builder-basic-field-list-item">
			<span role="presentation" onKeyDown={handleKeyDown}>
				{children}
			</span>
		</ListItemChild>
	) : (
		<StyledChild onKeyDown={handleKeyDown}>{children}</StyledChild>
	);
};

export const Field = ({
	fieldToRender,
	onSearchWrapper,
	onSwitchToJql,
	fieldsToRender,
	loading,
	hydratedData,
	selectedFields,
	onClearPicker,
	projectOptions,
	singleSelectFields = [],
	shouldUseListMarkup,
	hideOperatorDropdown,
	contextJql,
	viewContext,
	scope,
	suggestedUsersCount,
	isAddingField,
	setIsAddingField,
}: FieldProps): React.JSX.Element => {
	const { clause, secondaryClause, fieldId, fieldLabel, picker, fieldDescription, isIrremovable } =
		fieldToRender;

	const sendOptionAnalytics = useSendOptionAnalytics();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { pickerType, propType, fieldType } = picker;
	const itemListRef = useRef<HTMLUListElement>(null);

	const [activePopupKey, { openPopup, closePopup, closeAllPopups }] = useActivePopupKey();
	const onOpen = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdown',
		});
		const attributes: Attributes = {
			actionType: 'open',
		};
		fireUIAnalytics(analyticsEvent, 'refinementBarBasicPicker', attributes);

		return openPopup(fieldId);
	}, [createAnalyticsEvent, fieldId, openPopup]);

	const FieldWrapper = fg('list_lovability_improving_filters') ? FieldWrapperNew : FieldWrapperOld;

	const onClose = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'dropdown',
		});
		const attributes: Attributes = {
			actionType: 'close',
		};
		fireUIAnalytics(analyticsEvent, 'refinementBarBasicPicker', attributes);

		return closePopup(fieldId);
	}, [closePopup, fieldId, createAnalyticsEvent]);

	const onClearCallback = useCallback(
		(onClearJqlClause: string | undefined) => {
			const newSelectedFields = selectedFields.filter(
				(selectedField) => selectedField.value.toLowerCase() !== fieldId.toLowerCase(),
			);

			if (onClearJqlClause !== undefined) {
				onClearPicker({ selectedFields: newSelectedFields });
				onSearchWrapper && onSearchWrapper(onClearJqlClause);
			} else {
				onClearPicker({
					selectedFields: newSelectedFields,
					fieldsToRender: fieldsToRender.filter((field) => fieldId.toLowerCase() !== field.fieldId),
				});
			}
		},
		[fieldId, fieldsToRender, onClearPicker, onSearchWrapper, selectedFields],
	);

	const handleOnKeyDown = (event: React.KeyboardEvent<HTMLDivElement | HTMLSpanElement>) => {
		if (event.key === 'Escape') {
			// avoid closing a modal on closing a picker with esc key
			const isOpen = activePopupKey === fieldId;
			isOpen && event.stopPropagation();
			onClose();
		}
	};

	const UnsupportedFieldPicker = (
		<FieldWrapper
			useListElement={Boolean(shouldUseListMarkup)}
			handleKeyDown={handleOnKeyDown}
			ref={fg('list_lovability_improving_filters') ? itemListRef : undefined}
		>
			<UnsupportedPickerWrapper
				activePopupKey={activePopupKey}
				clause={clause}
				fieldDescription={fieldDescription}
				fieldId={fieldId}
				fieldLabel={fieldLabel}
				onClose={onClose}
				onOpen={onOpen}
				onClear={onClearCallback}
				onSwitchToJql={onSwitchToJql}
			/>
		</FieldWrapper>
	);

	useEffect(() => {
		if (isAddingField && itemListRef && fg('list_lovability_improving_filters')) {
			itemListRef?.current?.scrollIntoView?.({
				behavior: 'smooth',
				block: 'end',
			});
			openPopup(fieldId);
			setIsAddingField?.(false);
		}
	}, [setIsAddingField, isAddingField, openPopup, fieldId]);

	switch (pickerType) {
		case PickerTypes.Picker: {
			if (clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}

			return (
				<ContextualAnalyticsData
					sourceName="valueSearch"
					sourceType={INLINE_DIALOG}
					attributes={analyticsAttributes}
				>
					<FieldWrapper
						useListElement={Boolean(shouldUseListMarkup)}
						handleKeyDown={handleOnKeyDown}
						ref={fg('list_lovability_improving_filters') ? itemListRef : undefined}
					>
						<PickerWrapper
							onSearchWrapper={onSearchWrapper}
							fieldId={fieldId}
							fieldLabel={fieldLabel}
							picker={picker}
							terminalClause={clause || undefined}
							activePopupKey={activePopupKey}
							onOpen={onOpen}
							onClose={onClose}
							onSendOptionAnalytics={sendOptionAnalytics}
							loading={loading}
							hydratedData={hydratedData}
							onClear={onClearCallback}
							projectOptions={projectOptions}
							isMulti={!singleSelectFields.includes(fieldId)}
							fieldDescription={fieldDescription}
							onOpenOperatorPicker={closeAllPopups}
							hideOperatorDropdown={hideOperatorDropdown}
							isIrremovable={isIrremovable}
							contextJql={contextJql}
							viewContext={viewContext}
							scope={scope}
							suggestedUsersCount={suggestedUsersCount}
						/>
					</FieldWrapper>
				</ContextualAnalyticsData>
			);
		}
		case PickerTypes.TextArea: {
			return (
				<FieldWrapper
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
					ref={fg('list_lovability_improving_filters') ? itemListRef : undefined}
				>
					<TextAreaPicker
						clause={clause}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						fieldType={fieldType}
						activePopupKey={activePopupKey}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						onOpen={onOpen}
						onClose={onClose}
						fieldDescription={fieldDescription}
						propType={propType}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapper>
			);
		}
		case PickerTypes.DateTime:
		case PickerTypes.DueDate:
		case PickerTypes.DateTimeRange: {
			if (
				(clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) ||
				(secondaryClause && secondaryClause.clauseType !== CLAUSE_TYPE_TERMINAL)
			) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}

			return (
				<FieldWrapper
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
					ref={fg('list_lovability_improving_filters') ? itemListRef : undefined}
				>
					<DatePickerWrapper
						clause={clause}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						pickerType={pickerType}
						activePopupKey={activePopupKey}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						onOpen={onOpen}
						onClose={onClose}
						secondaryClause={secondaryClause}
						fieldDescription={fieldDescription}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapper>
			);
		}
		case PickerTypes.CascadeSelect: {
			if (clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}
			return (
				<FieldWrapper
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
					ref={fg('list_lovability_improving_filters') ? itemListRef : undefined}
				>
					<CascadeSelectPicker
						activePopupKey={activePopupKey}
						clause={clause}
						fieldDescription={fieldDescription}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						hydratedData={hydratedData}
						onClose={onClose}
						onOpen={onOpen}
						onSearchWrapper={onSearchWrapper}
						onSendOptionAnalytics={sendOptionAnalytics}
						onClear={onClearCallback}
						isIrremovable={isIrremovable}
						contextJql={contextJql}
					/>
				</FieldWrapper>
			);
		}
		case PickerTypes.NumberRange: {
			if (
				(clause && clause.clauseType !== CLAUSE_TYPE_TERMINAL) ||
				(secondaryClause && secondaryClause.clauseType !== CLAUSE_TYPE_TERMINAL)
			) {
				throw new Error(`Picker type ${pickerType} cannot be used with a non terminal clause`);
			}
			return (
				<FieldWrapper
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
					ref={fg('list_lovability_improving_filters') ? itemListRef : undefined}
				>
					<NumberRangePicker
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						activePopupKey={activePopupKey}
						clause={clause}
						secondaryClause={secondaryClause}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						onOpen={onOpen}
						onClose={onClose}
						fieldDescription={fieldDescription}
						onSendOptionAnalytics={sendOptionAnalytics}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapper>
			);
		}
		case PickerTypes.BooleanType: {
			return (
				<FieldWrapper
					useListElement={Boolean(shouldUseListMarkup)}
					handleKeyDown={handleOnKeyDown}
					ref={fg('list_lovability_improving_filters') ? itemListRef : undefined}
				>
					<LastUserCommentedPickerWrapper
						activePopupKey={activePopupKey}
						clause={clause}
						fieldDescription={fieldDescription}
						fieldId={fieldId}
						fieldLabel={fieldLabel}
						onClose={onClose}
						onOpen={onOpen}
						onSearchWrapper={onSearchWrapper}
						onClear={onClearCallback}
						isIrremovable={isIrremovable}
					/>
				</FieldWrapper>
			);
		}
		default:
			return UnsupportedFieldPicker;
	}
};

export default Field;
